import { useState, useEffect } from "react";

export interface NetworkStatus {
  /** Whether the device is currently online */
  isOnline: boolean;

  /** Whether the network connection appears to be working (can reach servers) */
  isConnected: boolean;

  /** Connection type if available */
  connectionType?: string;

  /** Last time we successfully connected */
  lastConnectedAt?: Date;
}

export interface UseNetworkStatusOptions {
  /** Interval to check connectivity (in milliseconds) */
  checkInterval?: number;

  /** URL to ping for connectivity test */
  testUrl?: string;

  /** Timeout for connectivity test (in milliseconds) */
  testTimeout?: number;

  /** Whether to perform periodic connectivity tests */
  enableConnectivityTest?: boolean;
}

export const useNetworkStatus = (options: UseNetworkStatusOptions = {}) => {
  const {
    checkInterval = 30000, // Check every 30 seconds
    testUrl = import.meta.env.VITE_GATEWAY_ENDPOINT,
    testTimeout = 5000,
    enableConnectivityTest = true,
  } = options;

  // For TV browsers, we'll use a simplified approach - assume online initially and rely on connectivity tests
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(() => {
    return {
      isOnline: true,
      isConnected: true,
      connectionType: (navigator as any)?.connection?.effectiveType,
      lastConnectedAt: new Date(),
    };
  });

  // Test actual connectivity to server
  const testConnectivity = async (): Promise<boolean> => {
    let isConnected = false;

    try {
      // Create a promise that will definitely resolve or reject
      const fetchPromise = new Promise<Response>((resolve, reject) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          //controller.abort();
          //reject(new Error("Network timeout"));
        }, testTimeout);

        fetch(testUrl, {
          method: "HEAD",
          signal: controller.signal,
          cache: "no-cache",
          mode: "cors",
        })
          .then((response) => {
            clearTimeout(timeoutId);
            resolve(response);
          })
          .catch((error) => {
            clearTimeout(timeoutId);
            reject(error);
          });
      });

      const response = await fetchPromise;
      isConnected = response.ok;
    } catch (error: any) {
      console.log("Network: Connectivity test caught error:", error);
      isConnected = false;
    }
    return isConnected;
  };

  // Update network status based on connectivity test results
  const performConnectivityTest = async () => {
    if (!enableConnectivityTest) {
      return;
    }

    const isConnected = await testConnectivity();
    const now = new Date();

    setNetworkStatus((prev) => {
      const newState = {
        ...prev,
        isOnline: isConnected,
        isConnected: isConnected,
        connectionType: (navigator as any)?.connection?.effectiveType,
        lastConnectedAt: isConnected ? now : prev.lastConnectedAt,
      };

      return newState;
    });
  };

  // Set up periodic connectivity testing
  useEffect(() => {
    if (!enableConnectivityTest) {
      return;
    }

    // Perform initial test quickly
    const initialTestTimeout = setTimeout(() => {
      performConnectivityTest();
    }, 1000);

    // Set up regular interval
    const interval = setInterval(() => {
      performConnectivityTest();
    }, checkInterval);

    return () => {
      clearTimeout(initialTestTimeout);
      clearInterval(interval);
    };
  }, [checkInterval, enableConnectivityTest, testUrl, testTimeout]);

  // Also run additional tests in first 30 seconds for faster startup detection
  useEffect(() => {
    if (!enableConnectivityTest) {
      return;
    }
    // Additional tests at 3s, 7s, 15s for faster detection
    const startupTests = [3000, 7000, 15000];
    const timeouts: NodeJS.Timeout[] = [];

    startupTests.forEach((delay) => {
      const timeout = setTimeout(() => {
        performConnectivityTest();
      }, delay);
      timeouts.push(timeout);
    });

    return () => {
      timeouts.forEach(clearTimeout);
    };
  }, [enableConnectivityTest]);

  // Manual connectivity check
  const checkConnectivity = async () => {
    await performConnectivityTest();
  };

  // Manual refresh
  const refresh = () => {
    performConnectivityTest();
  };

  return {
    ...networkStatus,
    checkConnectivity,
    refresh,
  };
};
